import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ChartTypes } from '@mode/shared/contract-common';
import { FormatListItem } from '../format-list-item';

@Component({
  selector: 'mode-format-builder-axis',
  templateUrl: './axis.component.html',
  styleUrls: ['./axis.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AxisComponent implements OnChanges {
  @Input() field!: FormatListItem;
  @Input() axisFormat: ChartTypes.AxisFieldFormat | undefined;
  @Output() update = new EventEmitter<ChartTypes.AxisFieldFormat>();

  title = {} as ChartTypes.TitleFormat;
  scale: ChartTypes.ScaleFormat = {};
  label: ChartTypes.LabelFormat = {};
  binnedAxisLabels: ChartTypes.BinnedAxisLabelTypeFormat = { type: ChartTypes.BinnedAxisLabelTypes.Auto };

  axisStartDate: Date | undefined;
  axisEndDate: Date | undefined;

  updateTitle(title: ChartTypes.TitleFormat) {
    this.title = { ...this.title, ...title };
    this.update.emit({
      title: this.title,
    });
  }

  updateScale(scale: ChartTypes.ScaleFormat) {
    this.scale = { ...this.scale, ...scale };
    this.update.emit({
      scale: this.scale,
    });
  }

  updateRotation(rotation: number) {
    this.label = { ...this.label, rotation };
    this.update.emit({
      label: this.label,
    });
  }

  updateBinnedAxisLabels(binnedAxisLabels: ChartTypes.BinnedAxisLabelTypeFormat) {
    this.update.emit({
      binnedAxisLabel: binnedAxisLabels,
    });
  }

  onAxisStartDateChange(date: Date | null) {
    const updatedScale: ChartTypes.ScaleFormat = {
      ...this.scale,
      range: {
        ...(this.scale.range ?? { isIndependent: false, max: undefined }),
        min: this.normalizeDateAsTimestamp(date),
      },
    };
    this.updateScale(updatedScale);
  }

  onAxisEndDateChange(date: Date | null) {
    const updatedScale: ChartTypes.ScaleFormat = {
      ...this.scale,
      range: {
        ...(this.scale.range ?? { isIndependent: false, min: undefined }),
        max: this.normalizeDateAsTimestamp(date),
      },
    };
    this.updateScale(updatedScale);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.axisFormat) {
      const format: ChartTypes.AxisFieldFormat = changes.axisFormat.currentValue ?? {};
      this.title = format.title ?? ({} as ChartTypes.TitleFormat);
      this.scale = format.scale ?? ({} as ChartTypes.ScaleFormat);
      this.label = format.label ?? ({} as ChartTypes.LabelFormat);
      this.binnedAxisLabels = format[ChartTypes.FieldAxisTypes.BinnedAxisLabel] ?? {
        type: ChartTypes.BinnedAxisLabelTypes.Auto,
      };

      this.axisStartDate = this.scale.range?.min != null ? new Date(this.scale.range.min) : undefined;
      this.axisEndDate = this.scale.range?.max != null ? new Date(this.scale.range.max) : undefined;
    }
  }

  private normalizeDateAsTimestamp(date: Date | null) {
    const normalizedDate = date != null ? new Date(date.getTime()).setUTCHours(0, 0, 0, 0) : null;
    return normalizedDate !== null && !Number.isNaN(normalizedDate) ? normalizedDate : undefined;
  }
}
